/**
 * The garden's postcode, whether Grow can plan from it, and the garden's own
 * day, as the server decides them (GET /garden-place, api/gardenPlace;
 * architecture audit batch 5, A29 and A31). The app judges nothing: no shape
 * check, no postcode of the member's own, no phone's day.
 *
 * In a shared garden it is the owner's postcode and the owner's day: a member
 * with no ZIP of their own, or a different one, sees the owner's garden with
 * its tabs and weather, and is never asked for a postcode (the owner's
 * profile holds it: own is false, and Today says so instead of the box).
 *
 * A postcode saved before the backend checked them can be one it cannot use
 * (a UK or Canadian code, a ZIP that is not a real place): the server says
 * why (reason), and Today asks for it again, saying so; the other tabs stay
 * hidden until it is given, as for a new account.
 *
 * Until the answer is in, nothing is known (known false): the tabs wait, and
 * Today shows neither the postcode step nor the day's lists. A failure on our
 * side is passed on (isError, error) for Today to say the standard sentence.
 *
 * A member is also handed the owner's share identity (ownerShare: the owner's
 * handles and garden address), which the share studio prints in place of the
 * member's own (B37); null in the person's own garden. (What only the owner
 * may change is locked by useOwnerOnly, on the one member signal.)
 *
 * Who uses it: the Layout's Header and TabBar (which tabs show), Today
 * (TodayPage: asking for the postcode; useTodayBrief, useGardenSeason), the
 * plant outlook (usePlantOutlook), the forecast (WeatherContext), the frost
 * card (FrostOutlook), the plants list (PlantsContext), the plants brief,
 * Seasons, the packet review and the stage sheet (the garden's day).
 * Cached under gardenPlaceKey (the garden being tended and the person's own
 * saved postcode, so saving one reads it again) for QUERY_STALE_TIME_MS, and
 * read again at the garden's next midnight (validUntil), so a page left open
 * rolls over to the new day.
 *
 * @module useGardenPostcode
 */

import { retryRule } from '@/config/queryRetry';
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { useTranslation } from './useTranslation';
import { chosenGarden } from '@/shared/api/gardenMembers';
import { getGardenPlace, gardenPlaceKey, type OwnerShare } from '@/shared/api/gardenPlace';
import { postcodeRefusalWords } from '@/shared/api/postcodes';
import { msUntilValid } from '@/shared/lib/briefFreshness';
import { QUERY_STALE_TIME_MS } from '@/config/queryClient';
import { QUERY_RETRY_COUNT } from '@/config/tunables/shared';

/** The garden's postcode as the server has it, whether it can be planned from, and the garden's day. */
export interface GardenPostcode {
  /** The garden's postcode as saved ('' when there is none, or until it is known): the owner's in a shared garden. */
  zip: string;
  /** Whether Grow can plan from it, as the server says; false until it is known. */
  usable: boolean;
  /** Why the garden cannot be planned, to say over the box (or in its place for a member); null when it can, or there is no postcode to speak of. */
  problem: string | null;
  /** Whether the person signed in owns the garden: only the owner can give it a postcode. True until known. */
  own: boolean;
  /** The garden's own day, "YYYY-MM-DD", from its time zone; '' until known. */
  date: string;
  /** The place's town, when it can be planned (the welcome's "Sparta, TN"); null otherwise. */
  city: string | null;
  /** The place's state, when it can be planned. */
  state: string | null;
  /** For a member, the owner's handles and garden address, for the share studio (B37); null in their own garden or until known. */
  ownerShare: OwnerShare | null;
  /** B64: the garden has had a plant of its own, ever (archived ones count); false is a new garden. False until known too: nothing reads it before `known` (Today's doors wait for it). */
  started: boolean;
  /** True once the server has answered. */
  known: boolean;
  /** True when the answer could not be had. */
  isError: boolean;
  /** The failure itself, or null. */
  error: unknown;
}

/**
 * The garden being tended's postcode and day, as the server says them.
 *
 * @returns The postcode, whether it can be used, why not, whose garden it is, and its day.
 */
export function useGardenPostcode(): GardenPostcode {
  const { reactTranslate: t } = useTranslation();
  const { mysqlUser } = useAuth();
  const queryClient = useQueryClient();
  const gardenId = chosenGarden() ?? mysqlUser?.user_id ?? null;
  const queryKey = gardenPlaceKey(gardenId, mysqlUser?.postal_code ?? '');
  const keyText = JSON.stringify(queryKey);
  const { data, isError, error } = useQuery({
    queryKey,
    queryFn: getGardenPlace,
    enabled: gardenId !== null,
    staleTime: QUERY_STALE_TIME_MS,
    retry: retryRule(QUERY_RETRY_COUNT),
  });

  // At the garden's midnight its day is another: read the place again then, so a page left open rolls over.
  const validUntil = data?.validUntil ?? null;
  useEffect(() => {
    const wait = msUntilValid(validUntil, Date.now());
    if (wait === null) return undefined;
    const timer = window.setTimeout(() => {
      // Not cancelling a read already under way: every reader of the hook sets this timer.
      void queryClient.invalidateQueries({ queryKey: JSON.parse(keyText) as unknown[], exact: true }, { cancelRefetch: false });
    }, wait);
    return () => window.clearTimeout(timer);
  }, [validUntil, keyText, queryClient]);

  if (!data) {
    return { zip: '', usable: false, problem: null, own: true, date: '', city: null, state: null, ownerShare: null, started: false, known: false, isError, error: isError ? error : null };
  }
  const { zip, usable, reason, message, own, date, city, state } = data;
  /** Why it cannot be planned: a member is told the owner gives it; the owner, why the saved one is refused. */
  const problem = usable ? null
    : !own ? t('welcome.zip.ownersGarden')
      : reason === 'FORMAT' ? t('welcome.zip.notUs', { zip })
        : reason ? postcodeRefusalWords(reason, zip, message ?? '')
          : null;
  return { zip, usable, problem, own, date, city, state, ownerShare: data.ownerShare, started: data.started, known: true, isError: false, error: null };
}
